(() => {
  const current = new URL(window.location.href);
  const previewThemeId = current.searchParams.get('preview_theme_id');
  if (!previewThemeId) return;

  const previewParameters = ['preview_theme_id', '_fd', 'pb'];

  const preservePreview = (anchor) => {
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const rawHref = anchor.getAttribute('href');
    if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) return;

    let destination;
    try {
      destination = new URL(rawHref, window.location.origin);
    } catch {
      return;
    }
    if (destination.origin !== window.location.origin) return;
    if (destination.pathname.startsWith('/admin') || destination.pathname.startsWith('/checkout')) return;

    previewParameters.forEach((name) => {
      const value = current.searchParams.get(name);
      if (value && !destination.searchParams.has(name)) destination.searchParams.set(name, value);
    });
    anchor.href = destination.href;
  };

  const updateLinks = (scope = document) => {
    if (scope instanceof HTMLAnchorElement) preservePreview(scope);
    scope.querySelectorAll?.('a[href]').forEach(preservePreview);
  };

  updateLinks();
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node instanceof Element) updateLinks(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Load the greeting-card four-surface compatibility layer only on maker routes.
   Kept outside the preview-only block so it also runs on normal storefront visits. */
(() => {
  const ROOT = '[data-ops-maker][data-preset="greeting-card-square"]';
  const sourceScript = document.currentScript || [...document.scripts].find((node) => /ops-preview-navigation\.js(?:\?|$)/.test(node.src || ''));
  const sourceUrl = sourceScript?.src || '';

  const greetingCardAssetUrl = () => {
    if (!sourceUrl) return '';
    try {
      const url = new URL(sourceUrl);
      url.pathname = url.pathname.replace(/ops-preview-navigation\.js$/, 'ops-greeting-card-four-surface.js');
      url.search = '';
      return url.href;
    } catch {
      return '';
    }
  };

  const hasGreetingCardMaker = (scope = document) => Boolean(
    scope.matches?.(ROOT) || scope.querySelector?.(ROOT)
  );

  const loadGreetingCardLayer = (scope = document) => {
    if (!hasGreetingCardMaker(scope)) return;
    if (document.querySelector('script[data-ops-greeting-card-four-surface-loader]')) return;
    const src = greetingCardAssetUrl();
    if (!src) return;
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.opsGreetingCardFourSurfaceLoader = '';
    document.head.append(script);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => loadGreetingCardLayer(), { once: true });
  } else {
    loadGreetingCardLayer();
  }

  document.addEventListener('shopify:section:load', (event) => loadGreetingCardLayer(event.target));
})();

/* Add a direct route from the custom greeting-card product page into the full maker. */
(() => {
  const PRODUCT_SELECTOR = 'product-info[data-url^="/products/custom-printed-greeting-cards"]';
  const CTA_SELECTOR = '[data-ops-greeting-card-maker-cta]';

  const ensureStyles = () => {
    if (document.querySelector('style[data-ops-greeting-card-maker-cta-styles]')) return;
    const style = document.createElement('style');
    style.dataset.opsGreetingCardMakerCtaStyles = '';
    style.textContent = `
      .ops-greeting-card-maker-cta { margin: 1.2rem 0 1.4rem; }
      .ops-greeting-card-maker-cta__button { width: 100%; min-height: 4.8rem; }
    `;
    document.head.append(style);
  };

  const addMakerButton = (scope = document) => {
    const productInfo = scope.matches?.(PRODUCT_SELECTOR)
      ? scope
      : scope.querySelector?.(PRODUCT_SELECTOR);
    if (!productInfo || productInfo.querySelector(CTA_SELECTOR)) return;

    const uploadBlock = productInfo.querySelector(
      '.custom-upload-block[data-product-handle="custom-printed-greeting-cards"]'
    );
    if (!uploadBlock) return;

    ensureStyles();
    const wrapper = document.createElement('div');
    wrapper.className = 'ops-greeting-card-maker-cta';
    wrapper.dataset.opsGreetingCardMakerCta = '';

    const link = document.createElement('a');
    link.href = '/pages/greeting-card-maker';
    link.className = 'button button--secondary ops-greeting-card-maker-cta__button';
    link.textContent = 'Go to Greeting Card Maker';

    wrapper.append(link);
    uploadBlock.before(wrapper);
  };

  const initialise = () => addMakerButton();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => addMakerButton(event.target));

  new MutationObserver((mutations) => {
    if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        addMakerButton(node);
        if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

(() => {
  const makerPath = '/pages/die-cut-sticker-maker';
  if (window.location.pathname.replace(/\/$/, '') !== makerPath) return;

  const DB_NAME = 'ops-diecut-maker-projects';
  const STORE_NAME = 'projects';
  const PROJECT_VERSION = 1;

  const openDatabase = () => new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('Saved Maker projects are not available in this browser.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('The saved Maker project could not be opened.'));
  });

  const readProject = async (id) => {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const request = transaction.objectStore(STORE_NAME).get(id);
      transaction.oncomplete = () => resolve(request.result || null);
      transaction.onerror = () => reject(transaction.error || request.error || new Error('The saved Maker project could not be read.'));
    });
  };

  const writeProject = async (record) => {
    const database = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(record);
      transaction.oncomplete = () => resolve(record);
      transaction.onerror = () => reject(transaction.error || new Error('The Maker project could not be saved.'));
    });
  };

  const makeProjectId = () => `diecut-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

  const currentProjectId = (tool) => {
    const url = new URL(window.location.href);
    return url.searchParams.get('opsProject') || tool.dataset.opsProjectId || '';
  };

  const projectUrl = (id) => {
    const url = new URL(window.location.href);
    url.searchParams.set('opsProject', id);
    url.searchParams.delete('opsArtwork');
    url.searchParams.delete('opsQr');
    url.searchParams.delete('opsQrSize');
    url.searchParams.delete('opsQrShape');
    return url;
  };

  const saveProject = async (tool) => {
    const source = tool.originalFile instanceof Blob ? tool.originalFile : tool.sourceFile;
    if (!(source instanceof Blob)) throw new Error('There is no Maker artwork to save yet.');

    const existingId = currentProjectId(tool);
    const id = existingId || makeProjectId();
    const existing = existingId ? await readProject(existingId).catch(() => null) : null;
    const sourceName = source.name || tool.sourceFilename || 'sticker-artwork.png';
    const file = source instanceof File
      ? source
      : new File([source], sourceName, { type: source.type || 'image/png', lastModified: Date.now() });
    const options = JSON.parse(JSON.stringify(tool.options || {}));
    const finish = String(tool.els?.orderFinish?.value || 'Gloss');
    const quantity = Math.max(1, Math.round(Number(tool.orderQuantity) || 1));
    const now = new Date().toISOString();

    await writeProject({
      id,
      version: PROJECT_VERSION,
      kind: 'die-cut-maker-project',
      name: tool.sourceFilename || sourceName,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      route: makerPath,
      file,
      sourceFilename: tool.sourceFilename || sourceName,
      options,
      finish,
      quantity
    });

    tool.dataset.opsProjectId = id;
    const url = projectUrl(id);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    return id;
  };

  const restoreProject = async (tool, id) => {
    const record = await readProject(id);
    if (!record) throw new Error('That saved Maker project is no longer available on this device.');
    const source = record.file;
    if (!(source instanceof Blob)) throw new Error('The saved Maker artwork is no longer available on this device.');

    const filename = record.sourceFilename || source.name || 'saved-sticker-artwork.png';
    const file = source instanceof File
      ? source
      : new File([source], filename, { type: source.type || 'image/png', lastModified: Date.now() });

    Object.assign(tool.options, record.options || {});
    tool.dataset.opsProjectId = id;
    await tool.loadFile(file);
    Object.assign(tool.options, record.options || {});

    if (tool.els?.orderFinish && record.finish) {
      const wanted = String(record.finish).toLowerCase();
      const option = Array.from(tool.els.orderFinish.options || []).find((entry) => String(entry.value).toLowerCase() === wanted);
      if (option) tool.els.orderFinish.value = option.value;
    }
    if (typeof tool.setOrderQuantity === 'function') tool.setOrderQuantity(record.quantity || 1, false);
    tool.updateInterface?.();
    tool.render?.();
    tool.updateOrderState?.();
    tool.showStatus?.('Saved Maker project reopened. You can change it or add it to your basket again.', 5000);
  };

  const installStableStickerScale = (tool) => {
    if (!tool || tool.dataset.opsStableStickerScale === 'true') return;
    if (typeof tool.pxPerMmEstimate !== 'function' || typeof tool.geometricArtworkScale !== 'function') return;

    tool.dataset.opsStableStickerScale = 'true';

    const referenceSizeMm = () => Math.max(10, Number(tool.settings?.maximumSizeMm) || 200);

    tool.pxPerMmEstimate = function() {
      if (!this.processedArtwork) return 1;
      const longest = Math.max(this.processedArtwork.width, this.processedArtwork.height);
      const reference = referenceSizeMm();
      const interiorMm = this.options.border
        ? Math.max(2, reference - Number(this.options.borderMm || 0) * 2)
        : reference;
      return longest / interiorMm;
    };

    tool.geometricArtworkScale = function(dimensions) {
      const reference = referenceSizeMm();
      const borderPixels = this.options.border
        ? Math.max(0, Number(this.options.borderMm || 0) * (dimensions.shapeWidth / reference))
        : 0;
      const availableWidth = Math.max(1, dimensions.shapeWidth - borderPixels * 2);
      const availableHeight = Math.max(1, dimensions.shapeHeight - borderPixels * 2);
      const contain = Math.min(availableWidth / this.processedArtwork.width, availableHeight / this.processedArtwork.height);
      if (this.options.fit === 'fill') return Math.max(availableWidth / this.processedArtwork.width, availableHeight / this.processedArtwork.height);
      if (this.options.fit === 'edge') return contain;
      return contain * 0.88;
    };

    tool.maskCache?.clear();
    if (tool.processedArtwork) tool.render?.();
  };

  const install = (tool) => {
    if (!tool) return;
    installStableStickerScale(tool);
    if (tool.dataset.opsProjectLinkReady === 'true') return;
    if (typeof tool.addToCart !== 'function' || typeof tool.loadFile !== 'function') return;
    tool.dataset.opsProjectLinkReady = 'true';

    const originalAddToCart = tool.addToCart.bind(tool);
    tool.addToCart = async (...args) => {
      try {
        await saveProject(tool);
      } catch (error) {
        console.warn('The Maker project could not be saved for reopening. The order can still continue.', error);
      }
      return originalAddToCart(...args);
    };

    const id = new URL(window.location.href).searchParams.get('opsProject');
    if (id) {
      restoreProject(tool, id).catch((error) => {
        console.warn(error);
        tool.showError?.(error.message || 'That saved Maker project could not be reopened.');
      });
    }
  };

  let attempts = 0;
  const scan = () => {
    const tool = document.querySelector('ops-die-cut-proof-tool');
    if (tool) install(tool);
    if ((!tool || tool.dataset.opsProjectLinkReady !== 'true' || tool.dataset.opsStableStickerScale !== 'true') && attempts < 120) {
      attempts += 1;
      window.setTimeout(scan, 50);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();

/* Add a direct route from the custom die-cut sticker product page into the full maker. */
(() => {
  const PRODUCT_SELECTOR = 'product-info[data-url^="/products/custom-stickers"]';
  const CTA_SELECTOR = '[data-ops-diecut-maker-cta]';

  const ensureStyles = () => {
    if (document.querySelector('style[data-ops-diecut-maker-cta-styles]')) return;
    const style = document.createElement('style');
    style.dataset.opsDiecutMakerCtaStyles = '';
    style.textContent = `
      ${PRODUCT_SELECTOR} .ops-diecut-maker-cta {
        margin: 1.2rem 0 1.4rem;
        max-width: 44rem;
      }
      ${PRODUCT_SELECTOR} .ops-diecut-maker-cta__button {
        width: 100%;
        min-height: 4.8rem;
      }
    `;
    document.head.append(style);
  };

  const addMakerButton = (scope = document) => {
    const productInfo = scope.matches?.(PRODUCT_SELECTOR)
      ? scope
      : scope.querySelector?.(PRODUCT_SELECTOR);
    if (!productInfo || productInfo.querySelector(CTA_SELECTOR)) return;

    const uploadBlock = productInfo.querySelector(
      '.custom-upload-block[data-product-handle="custom-stickers"]'
    );
    if (!uploadBlock) return;

    ensureStyles();
    const wrapper = document.createElement('div');
    wrapper.className = 'ops-diecut-maker-cta';
    wrapper.dataset.opsDiecutMakerCta = '';

    const link = document.createElement('a');
    link.href = productInfo.dataset.opsDiecutMakerUrl || '/pages/die-cut-sticker-maker';
    link.className = 'button button--secondary ops-diecut-maker-cta__button';
    link.textContent = 'Go to Sticker Maker';

    wrapper.append(link);
    uploadBlock.before(wrapper);
  };

  const initialise = () => addMakerButton();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => addMakerButton(event.target));

  new MutationObserver((mutations) => {
    if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        addMakerButton(node);
        if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Add a direct route from the custom sticker-sheet product page into the full maker. */
(() => {
  const PRODUCT_SELECTOR = 'product-info[data-url^="/products/custom-sticker-sheets"]';
  const CTA_SELECTOR = '[data-ops-sticker-sheet-maker-cta]';

  const ensureStyles = () => {
    if (document.querySelector('style[data-ops-sticker-sheet-maker-cta-styles]')) return;
    const style = document.createElement('style');
    style.dataset.opsStickerSheetMakerCtaStyles = '';
    style.textContent = `
      ${PRODUCT_SELECTOR} .ops-sticker-sheet-maker-cta {
        margin: 1.2rem 0 1.4rem;
        max-width: 44rem;
      }
      ${PRODUCT_SELECTOR} .ops-sticker-sheet-maker-cta__button {
        width: 100%;
        min-height: 4.8rem;
      }
    `;
    document.head.append(style);
  };

  const addMakerButton = (scope = document) => {
    const productInfo = scope.matches?.(PRODUCT_SELECTOR)
      ? scope
      : scope.querySelector?.(PRODUCT_SELECTOR);
    if (!productInfo || productInfo.querySelector(CTA_SELECTOR)) return;

    const uploadBlock = productInfo.querySelector(
      '.custom-upload-block[data-product-handle="custom-sticker-sheets"]'
    );
    if (!uploadBlock) return;

    ensureStyles();
    const wrapper = document.createElement('div');
    wrapper.className = 'ops-sticker-sheet-maker-cta';
    wrapper.dataset.opsStickerSheetMakerCta = '';

    const link = document.createElement('a');
    link.href = productInfo.dataset.opsStickerSheetMakerUrl || '/pages/sticker-sheet-maker';
    link.className = 'button button--secondary ops-sticker-sheet-maker-cta__button';
    link.textContent = 'Go to Sticker Sheet Maker';

    wrapper.append(link);
    uploadBlock.before(wrapper);
  };

  const initialise = () => addMakerButton();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => addMakerButton(event.target));

  new MutationObserver((mutations) => {
    if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        addMakerButton(node);
        if (document.querySelector(`${PRODUCT_SELECTOR} ${CTA_SELECTOR}`)) return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
